import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getContactsByCustomerId,
  getCustomerById,
  getEmails,
} from "../../api/Customer/customerApi";
import { fetchBudgetLines } from "../../api/Estimates/budgetLinesApi";
import {
  buildClientOfferBatchHtmlDraft,
  buildClientOfferBatchSubject,
  getUniboxAccounts,
  resolvePresupuestoTemporada,
  sendEstimateClientOfferBatch,
} from "../../api/Estimates/estimateRfqApi";
import { resolveOfferCurrencyFromLines } from "../../api/Estimates/clientOfferEmailHtml";
import {
  buildBudgetGroupedPdfFile,
  buildBudgetPdfFile,
} from "../../utils/printBudgetPdf";

const MAX_EXTRA_ATTACHMENTS = 8;
const MAX_ATTACHMENT_MB = 15;
const MAX_BATCH = 20;
const MIN_BATCH = 2;
const CLIENT_ACCENT = "#00c2a3";

function resolveModelo(presupuesto) {
  return (
    String(
      presupuesto?.DiseñoMuestra ||
        presupuesto?.NombrePresupuesto ||
        presupuesto?.designCliente ||
        "",
    ).trim() || "—"
  );
}

function resolveItemMoneda(p, monedaByPresupuesto = {}) {
  const id = Number(p?.IdPresupuesto);
  const fromMap = monedaByPresupuesto[id];
  if (fromMap) return fromMap;
  const fromBudget = String(p?.MonedaVenta || "").trim();
  if (fromBudget) return resolveOfferCurrencyFromLines([], fromBudget);
  return "EUR";
}

function resolveClientLabel(raw) {
  const name = String(raw || "").trim();
  if (!name || /^\d+$/.test(name)) return "";
  return name;
}

function parseEmailTokens(raw) {
  return String(raw || "")
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e && e.includes("@"));
}

function mergeEmailList(values) {
  const seen = new Set();
  const out = [];
  for (const v of values || []) {
    const raw = typeof v === "string" ? v : v?.email;
    for (const e of parseEmailTokens(raw)) {
      if (seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

function resolveBatchSeason(items) {
  const seasons = [
    ...new Set(
      (items || []).map((p) => resolvePresupuestoTemporada(p)).filter(Boolean),
    ),
  ];
  if (!seasons.length) return "";
  if (seasons.length === 1) return seasons[0];
  return seasons.slice(0, 3).join(" + ");
}

/**
 * Oferta conjunta: N presupuestos del mismo cliente → 1 email.
 */
export default function EstimateEnviarOfertaConjuntaDialog({
  open,
  onClose,
  presupuestos = [],
  onSent,
}) {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [contactEmails, setContactEmails] = useState([]);
  const [clienteNombre, setClienteNombre] = useState("");
  const [toEmails, setToEmails] = useState([]);
  const [ccEmail, setCcEmail] = useState("");
  const [lang, setLang] = useState("en");
  const [notas, setNotas] = useState("");
  const [subject, setSubject] = useState("");
  const [files, setFiles] = useState([]);
  const [attachGroupedPdf, setAttachGroupedPdf] = useState(true);
  const [attachIndividualPdfs, setAttachIndividualPdfs] = useState(true);
  const [buildingPdf, setBuildingPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [lineCounts, setLineCounts] = useState({});
  const [monedaByPresupuesto, setMonedaByPresupuesto] = useState({});
  const [loadingLines, setLoadingLines] = useState(false);

  const isEs = lang === "es";
  const items = useMemo(
    () => (Array.isArray(presupuestos) ? presupuestos.filter(Boolean) : []),
    [presupuestos],
  );

  const validation = useMemo(() => {
    if (items.length < MIN_BATCH) {
      return {
        ok: false,
        message: isEs
          ? `Selecciona al menos ${MIN_BATCH} presupuestos.`
          : `Select at least ${MIN_BATCH} estimates.`,
      };
    }
    if (items.length > MAX_BATCH) {
      return {
        ok: false,
        message: isEs
          ? `Máximo ${MAX_BATCH} presupuestos por lote.`
          : `Maximum ${MAX_BATCH} estimates per batch.`,
      };
    }
    const clientIds = [
      ...new Set(
        items.map((p) =>
          p.IdCliente == null || p.IdCliente === ""
            ? null
            : String(p.IdCliente),
        ),
      ),
    ];
    if (clientIds.length !== 1 || clientIds[0] == null) {
      return {
        ok: false,
        message: isEs
          ? "Todos los presupuestos deben ser del mismo cliente."
          : "All estimates must belong to the same client.",
      };
    }
    const withoutLines = items.filter(
      (p) => (lineCounts[Number(p.IdPresupuesto)] || 0) <= 0,
    );
    if (Object.keys(lineCounts).length && withoutLines.length) {
      return {
        ok: false,
        message: isEs
          ? `Sin líneas de escandallo: ${withoutLines
              .map((p) => p.NumeroPresupuesto || p.IdPresupuesto)
              .join(", ")}`
          : `No cost-sheet lines: ${withoutLines
              .map((p) => p.NumeroPresupuesto || p.IdPresupuesto)
              .join(", ")}`,
        withoutLines,
      };
    }
    return { ok: true, idCliente: clientIds[0] };
  }, [items, lineCounts, isEs]);

  const idCliente = validation.idCliente;
  const temporada = useMemo(() => resolveBatchSeason(items), [items]);
  const createdCount = items.filter(
    (p) => String(p.Estado || "") === "Created" || !p.Estado,
  ).length;
  const offeredCount = items.length - createdCount;
  const isResendOnly = createdCount === 0;

  const previewHtml = useMemo(
    () =>
      buildClientOfferBatchHtmlDraft({
        lang,
        items: items.map((p) => ({
          numeroPresupuesto: p.NumeroPresupuesto,
          nombrePresupuesto: p.NombrePresupuesto,
          diseno: resolveModelo(p),
          units: p.Units,
          precioUnitario: p.VentaUnitaria ?? p.VentaUN,
          totalVenta: p.Venta,
          moneda: resolveItemMoneda(p, monedaByPresupuesto),
        })),
        notas,
      }),
    [lang, items, notas, monedaByPresupuesto],
  );

  const emailOptions = useMemo(() => {
    const selected = new Set(toEmails.map((e) => e.toLowerCase()));
    const seen = new Set();
    const out = [];
    for (const e of contactEmails) {
      const email = String(e.email || "").trim();
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key) || selected.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
    return out;
  }, [contactEmails, toEmails]);

  const buildSubject = (clientName, langCode) =>
    buildClientOfferBatchSubject({
      cliente:
        resolveClientLabel(clientName) ||
        resolveClientLabel(items[0]?.ClienteNombre) ||
        resolveClientLabel(items[0]?.MarcaNombre) ||
        "",
      temporada,
      count: items.length,
      lang: langCode,
    });

  useEffect(() => {
    if (!open) return;
    setError("");
    setSending(false);
    setBuildingPdf(false);
    setCcEmail("");
    setNotas("");
    setFiles([]);
    setAttachGroupedPdf(true);
    setAttachIndividualPdfs(true);
    setLang("en");
    setLineCounts({});
    setMonedaByPresupuesto({});

    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const list = await getUniboxAccounts();
        if (cancelled) return;
        setAccounts(list);
        setFromEmail(list.find((a) => a?.email)?.email || "");
      } catch (err) {
        if (!cancelled) {
          setAccounts([]);
          setFromEmail("");
          setError(
            err?.response?.data?.error ||
              "No se pudieron cargar las cuentas MATRIXBOX.",
          );
        }
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !items.length) return;
    let cancelled = false;
    (async () => {
      setLoadingLines(true);
      try {
        const pairs = await Promise.all(
          items.map(async (p) => {
            const id = Number(p.IdPresupuesto);
            try {
              const lines = await fetchBudgetLines(id);
              const list = Array.isArray(lines) ? lines : [];
              return {
                id,
                count: list.length,
                moneda: resolveOfferCurrencyFromLines(
                  list,
                  p.MonedaVenta || "EUR",
                ),
              };
            } catch {
              return {
                id,
                count: 0,
                moneda: resolveItemMoneda(p, {}),
              };
            }
          }),
        );
        if (!cancelled) {
          setLineCounts(Object.fromEntries(pairs.map((x) => [x.id, x.count])));
          setMonedaByPresupuesto(
            Object.fromEntries(pairs.map((x) => [x.id, x.moneda])),
          );
        }
      } finally {
        if (!cancelled) setLoadingLines(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, items]);

  useEffect(() => {
    if (!open || !idCliente) {
      const safe = resolveClientLabel(items[0]?.ClienteNombre);
      setContactEmails([]);
      setClienteNombre(safe);
      setToEmails([]);
      setSubject(buildSubject(safe, lang));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [customer, contacts, emails] = await Promise.all([
          getCustomerById(idCliente).catch(() => null),
          getContactsByCustomerId(idCliente).catch(() => []),
          getEmails(idCliente).catch(() => []),
        ]);
        if (cancelled) return;
        const name = resolveClientLabel(
          customer?.name ||
            customer?.NombreCliente ||
            customer?.nombre ||
            customer?.Name ||
            items[0]?.ClienteNombre ||
            items[0]?.MarcaNombre ||
            "",
        );
        setClienteNombre(name);
        setSubject(buildSubject(name, lang));
        const list = [];
        for (const c of Array.isArray(contacts) ? contacts : []) {
          const email = String(c.Email || c.email || "").trim();
          if (!email) continue;
          list.push({
            email,
            label: c.Nombre || c.nombre || c.Name || email,
            source: "contact",
          });
        }
        for (const e of Array.isArray(emails) ? emails : []) {
          const email = String(e.Email || e.email || e.direccion || "").trim();
          if (!email) continue;
          list.push({
            email,
            label: e.Tipo || e.tipo || email,
            source: "email",
          });
        }
        const customerEmail = String(
          customer?.Email || customer?.email || "",
        ).trim();
        if (customerEmail) {
          list.unshift({
            email: customerEmail,
            label: isEs ? "Ficha cliente" : "Client card",
            source: "customer",
          });
        }
        setContactEmails(list);
        setToEmails(
          list[0]?.email ? [String(list[0].email).trim().toLowerCase()] : [],
        );
      } catch {
        if (!cancelled) {
          setContactEmails([]);
          setToEmails([]);
          const safe = resolveClientLabel(items[0]?.ClienteNombre);
          setClienteNombre(safe);
          setSubject(buildSubject(safe, lang));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCliente]);

  useEffect(() => {
    if (!open) return;
    setSubject((prev) => {
      const autoEs = buildSubject(clienteNombre, "es");
      const autoEn = buildSubject(clienteNombre, "en");
      if (!prev || prev === autoEs || prev === autoEn) {
        return buildSubject(clienteNombre, lang);
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, open, clienteNombre, temporada, items.length]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError("");
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_EXTRA_ATTACHMENTS) {
        setError(`Máximo ${MAX_EXTRA_ATTACHMENTS} adjuntos extra.`);
        break;
      }
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setError(`"${file.name}" supera ${MAX_ATTACHMENT_MB} MB.`);
        continue;
      }
      const dup = next.some(
        (f) => f.name === file.name && f.size === file.size,
      );
      if (!dup) next.push(file);
    }
    setFiles(next);
  };

  const handleSend = async () => {
    setError("");
    if (!validation.ok) {
      setError(validation.message);
      return;
    }
    if (!attachGroupedPdf && !attachIndividualPdfs) {
      setError(
        isEs
          ? "Elige al menos un tipo de PDF (agrupado o individuales)."
          : "Pick at least one PDF option (grouped or individual).",
      );
      return;
    }
    if (!fromEmail) {
      setError("Selecciona la cuenta de envío (MATRIXBOX).");
      return;
    }
    if (!toEmails.length) {
      setError(
        isEs
          ? "Indica al menos un email del cliente."
          : "Enter at least one client email.",
      );
      return;
    }

    setSending(true);
    try {
      const html = buildClientOfferBatchHtmlDraft({
        lang,
        items: items.map((p) => ({
          numeroPresupuesto: p.NumeroPresupuesto,
          nombrePresupuesto: p.NombrePresupuesto,
          diseno: resolveModelo(p),
          units: p.Units,
          precioUnitario: p.VentaUnitaria ?? p.VentaUN,
          totalVenta: p.Venta,
          moneda: resolveItemMoneda(p, monedaByPresupuesto),
        })),
        notas,
      });

      const attachList = [...files];
      setBuildingPdf(true);
      try {
        if (attachGroupedPdf) {
          const grouped = await buildBudgetGroupedPdfFile({
            idsPresupuestos: items.map((p) => p.IdPresupuesto),
            proyectos: items.map((p) => ({
              ...p,
              MonedaVenta: resolveItemMoneda(p, monedaByPresupuesto),
            })),
          });
          if (grouped) attachList.unshift(grouped);
        }
        if (attachIndividualPdfs) {
          for (const p of items) {
            const moneda = resolveItemMoneda(p, monedaByPresupuesto);
            const pdfFile = await buildBudgetPdfFile({
              idPresupuesto: p.IdPresupuesto,
              nombreProducto: resolveModelo(p),
              unidades: p.Units,
              proveedorNombre: p.ProveedorNombre,
              precioUnitario: p.VentaUnitaria ?? p.VentaUN,
              totalVenta: p.Venta,
              moneda,
              fechaPresupuesto:
                p.FechaPresentacion || p.FechaAceptacion || new Date(),
            });
            if (pdfFile) attachList.push(pdfFile);
          }
        }
      } catch (pdfErr) {
        throw new Error(
          pdfErr?.message ||
            "No se pudieron generar los PDF del lote para adjuntar.",
        );
      } finally {
        setBuildingPdf(false);
      }

      const result = await sendEstimateClientOfferBatch(
        {
          presupuestoIds: items.map((p) => p.IdPresupuesto),
          fromEmail,
          toEmail: toEmails.join(", "),
          ccEmail: String(ccEmail || "").trim(),
          subject,
          html,
          lang,
          clienteNombre,
          markOffered: "auto",
        },
        attachList,
      );

      if (typeof onSent === "function") {
        onSent(result);
      }
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo enviar la oferta conjunta",
      );
    } finally {
      setBuildingPdf(false);
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isResendOnly
          ? isEs
            ? "Reenviar oferta conjunta"
            : "Resend batch offer"
          : isEs
            ? "Enviar oferta conjunta"
            : "Send batch offer"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.75} sx={{ pt: 0.5 }}>
          <Alert severity="info">
            {isEs
              ? `Un solo email con ${items.length} estilos. Los que estén en Created pasarán a Offered si el envío OK (${createdCount}). Ya Offered: solo reenvío (${offeredCount}).`
              : `One email for ${items.length} styles. Created ones become Offered after send OK (${createdCount}). Already Offered: resend only (${offeredCount}).`}
          </Alert>

          {!validation.ok ? (
            <Alert severity="warning">{validation.message}</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              label={`${items.length} styles`}
              sx={{
                fontWeight: 800,
                bgcolor: `${CLIENT_ACCENT}22`,
                color: "#0f766e",
              }}
            />
            {clienteNombre ? (
              <Chip size="small" variant="outlined" label={clienteNombre} />
            ) : null}
            {temporada ? (
              <Chip size="small" variant="outlined" label={temporada} />
            ) : null}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={lang}
              onChange={(_e, v) => v && setLang(v)}
            >
              <ToggleButton value="en">EN</ToggleButton>
              <ToggleButton value="es">ES</ToggleButton>
            </ToggleButtonGroup>
            {loadingLines ? <CircularProgress size={16} /> : null}
          </Stack>

          <Box
            sx={{
              maxHeight: 160,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1,
            }}
          >
            <Stack spacing={0.75}>
              {items.map((p) => {
                const linesN = lineCounts[Number(p.IdPresupuesto)];
                const noLines = linesN != null && Number(linesN) <= 0;
                return (
                  <Stack
                    key={p.IdPresupuesto}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Chip
                      size="small"
                      label={p.NumeroPresupuesto || `#${p.IdPresupuesto}`}
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      {resolveModelo(p)}
                    </Typography>
                    <Chip
                      size="small"
                      label={p.Estado || "Created"}
                      color={
                        String(p.Estado) === "Offered" ? "success" : "default"
                      }
                      sx={{ height: 22 }}
                    />
                    {noLines ? (
                      <Chip size="small" color="warning" label="Sin líneas" />
                    ) : null}
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          <FormControl fullWidth size="small" disabled={loadingAccounts}>
            <InputLabel id="batch-offer-from">From (MATRIXBOX)</InputLabel>
            <Select
              labelId="batch-offer-from"
              label="From (MATRIXBOX)"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
            >
              {accounts.map((a) => (
                <MenuItem key={a.email || a.id} value={a.email}>
                  {a.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={emailOptions}
            value={toEmails}
            onChange={(_e, value) => setToEmails(mergeEmailList(value))}
            getOptionLabel={(o) => {
              if (typeof o === "string") return o;
              if (!o?.email) return "";
              return o.label && o.label !== o.email
                ? `${o.label} · ${o.email}`
                : o.email;
            }}
            isOptionEqualToValue={(a, b) => {
              const ea =
                typeof a === "string"
                  ? a.toLowerCase()
                  : String(a?.email || "").toLowerCase();
              const eb =
                typeof b === "string"
                  ? b.toLowerCase()
                  : String(b?.email || "").toLowerCase();
              return ea === eb;
            }}
            renderTags={(value, getTagProps) =>
              value.map((email, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={email} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={isEs ? "Para (emails)" : "To (emails)"}
                size="small"
                helperText={
                  isEs
                    ? "Elige contactos o escribe emails (Enter / coma)"
                    : "Pick contacts or type emails (Enter / comma)"
                }
              />
            )}
          />

          <TextField
            label="CC"
            size="small"
            fullWidth
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
          />

          <TextField
            label={isEs ? "Asunto" : "Subject"}
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <TextField
            label={isEs ? "Notas (en el cuerpo)" : "Notes (in body)"}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />

          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              {isEs ? "Vista previa del cuerpo" : "Body preview"}
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: "#fafafa",
                maxHeight: 280,
                overflow: "auto",
                "& table": {
                  width: "100% !important",
                  maxWidth: "100% !important",
                },
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </Box>

          <Stack spacing={0.25}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={attachGroupedPdf}
                  onChange={(e) => setAttachGroupedPdf(e.target.checked)}
                  sx={{
                    color: CLIENT_ACCENT,
                    "&.Mui-checked": { color: CLIENT_ACCENT },
                  }}
                />
              }
              label={
                isEs
                  ? "PDF resumen agrupado (recomendado)"
                  : "Grouped summary PDF (recommended)"
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={attachIndividualPdfs}
                  onChange={(e) => setAttachIndividualPdfs(e.target.checked)}
                  sx={{
                    color: CLIENT_ACCENT,
                    "&.Mui-checked": { color: CLIENT_ACCENT },
                  }}
                />
              }
              label={
                isEs
                  ? `PDF individual por estilo (${items.length})`
                  : `Individual PDF per style (${items.length})`
              }
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Button
              size="small"
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
            >
              {isEs ? "Más adjuntos" : "More attachments"}
              <input
                hidden
                type="file"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Button>
            {files.map((f, idx) => (
              <Chip
                key={`${f.name}-${idx}`}
                size="small"
                label={f.name}
                onDelete={() =>
                  setFiles((prev) => prev.filter((_, i) => i !== idx))
                }
                deleteIcon={<DeleteOutlineIcon />}
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          {isEs ? "Cancelar" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={
            sending ||
            buildingPdf ||
            loadingLines ||
            !validation.ok ||
            (!attachGroupedPdf && !attachIndividualPdfs)
          }
          sx={{
            bgcolor: CLIENT_ACCENT,
            fontWeight: 800,
            textTransform: "none",
            "&:hover": { bgcolor: "#00a88c" },
          }}
          startIcon={
            sending || buildingPdf ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {buildingPdf
            ? isEs
              ? "Generando PDFs…"
              : "Building PDFs…"
            : sending
              ? isEs
                ? "Enviando…"
                : "Sending…"
              : isResendOnly
                ? isEs
                  ? "Reenviar oferta conjunta"
                  : "Resend batch offer"
                : isEs
                  ? "Enviar oferta conjunta"
                  : "Send batch offer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
